import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Alert, Button, Table } from 'react-bootstrap';
import Select from 'react-select';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useCSVReader } from 'react-papaparse';
import { useTranslation } from 'react-i18next';

import { SubmitButton, PhoneCountrySelect, isValidEmail } from '../form';
import { get, post } from '../../lib/api';
import { ApplicationType } from '../../lib/applications';
import { combinePhone, splitPhone } from '../../lib/phone';

function toBool(val: unknown): boolean {
    return !!val && (val === 'TRUE' || val === 'true' || val === true || val === 1 || val === '1');
}

interface Row {
    firstName: string;
    lastName: string;
    phone: string; // E.164 ("+4798765432"); split to country code + number on submit
    email: string;
    office24SevenCustomerId: string;
    userName: string;
    sendMeEmail: boolean;
    applicationName: string;
    companyId: string;
    companyPassword: string;
    userPassword: string;
    storeId: string;
    user: string;
    isJasper: boolean;
    jasperName: string;
    mapToSso: boolean;
    mapToSsoName: string;
    key: number;
}

interface SyncResult {
    rowNumber: number;
    userName: string;
    applicationName: string;
    error: string;
}

const inputCls = (ok: boolean) => `form-control form-control-sm${ok ? '' : ' is-invalid'}`;

export function CsvImport() {
    const { t } = useTranslation();
    const { CSVReader } = useCSVReader();
    const [rows, setRows] = useState<Row[] | null>(null);
    const [checked, setChecked] = useState<boolean[]>([]);
    const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);
    const [success, setSuccess] = useState('');
    const [serverError, setServerError] = useState('');
    const [loading, setLoading] = useState(false);

    // Pre-registered values that some columns must be chosen from.
    const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
    const [ssInstances, setSsInstances] = useState<string[]>([]);
    const [jasperInstances, setJasperInstances] = useState<string[]>([]);

    useEffect(() => {
        get<{ customerId24Seven: string | number; name: string }[]>('Home/GetCustomers')
            .then((r) =>
                setCustomers(
                    (r.data ?? [])
                        .map((c) => ({
                            value: String(c.customerId24Seven),
                            label: `${c.customerId24Seven} - ${c.name}`,
                        }))
                        // Newest customer numbers first.
                        .sort((a, b) => Number(b.value) - Number(a.value)),
                ),
            )
            .catch(() => undefined);
        post<string[]>('home/getApplicationNames', { appType: ApplicationType.Smartstore })
            .then((r) => setSsInstances((r.data ?? []).slice().sort((a, b) => a.localeCompare(b, 'nb'))))
            .catch(() => undefined);
        post<string[]>('home/getApplicationNames', { appType: ApplicationType.Jasper })
            .then((r) => setJasperInstances((r.data ?? []).slice().sort((a, b) => a.localeCompare(b, 'nb'))))
            .catch(() => undefined);
    }, []);

    const ssOptions = useMemo(() => ssInstances.map((s) => ({ value: s, label: s })), [ssInstances]);
    const jasperOptions = useMemo(() => jasperInstances.map((s) => ({ value: s, label: s })), [jasperInstances]);

    function onUploadAccepted(results: { data: Record<string, string>[] }) {
        setSuccess('');
        setServerError('');
        setSyncResults(null);
        const mapped: Row[] = results.data.map((d, index) => ({
            firstName: d['first name'] ?? '',
            lastName: d['last name'] ?? '',
            // The CSV keeps country code + number in two columns; combine into one
            // E.164 phone for the editable field, split back apart on submit.
            phone: combinePhone(d['country code']?.trim() ?? '', d['mobile number']?.trim() ?? ''),
            email: d['email']?.trim() ?? '',
            office24SevenCustomerId: d['customerId']?.trim() ?? '',
            userName: d['email']?.trim() ?? '',
            sendMeEmail: false,
            applicationName: d['ssApplicationServer']?.trim() ?? '',
            companyId: d['ssCompany']?.trim() ?? '',
            companyPassword: d['ssCompanyPwd']?.trim() ?? '',
            userPassword: d['ssPwd']?.trim() ?? '',
            storeId: d['ssStoreId']?.trim() ?? '',
            user: d['ssUser']?.trim() ?? '',
            isJasper: toBool(d['isJasper']?.trim()),
            jasperName: d['jasperApplicationName']?.trim() ?? '',
            mapToSso: true,
            mapToSsoName: `map${index}`,
            key: index + 1,
        }));
        setRows(mapped);
        setChecked(mapped.map(() => true));
    }

    function toggle(index: number) {
        setChecked((prev) => prev.map((v, i) => (i === index ? !v : v)));
    }

    /** Patch one row in place (used by every editable cell). */
    function updateRow(index: number, patch: Partial<Row>) {
        setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : prev));
    }

    // A value is OK while the list is still loading (length 0) so cells don't flash red.
    const customerOk = (d: Row) =>
        customers.length === 0 || customers.some((c) => c.value === d.office24SevenCustomerId);
    const appOk = (d: Row) =>
        d.applicationName === ''
            ? d.isJasper && d.jasperName !== '' // empty is only valid for a Jasper-only row
            : ssInstances.length === 0 || ssInstances.includes(d.applicationName);
    const jasperOk = (d: Row) =>
        !d.isJasper || jasperInstances.length === 0 || jasperInstances.includes(d.jasperName);

    /** Whether every cell of a row passes client-side validation. */
    function rowValid(d: Row): boolean {
        return (
            d.firstName.trim() !== '' &&
            d.lastName.trim() !== '' &&
            isValidEmail(d.email) &&
            isValidPhoneNumber(d.phone) &&
            customerOk(d) &&
            appOk(d) &&
            jasperOk(d) &&
            d.companyId !== '' &&
            d.userPassword !== '' &&
            d.storeId !== '' &&
            d.user !== ''
        );
    }

    const invalidCount = (rows ?? []).filter((d, i) => checked[i] && !rowValid(d)).length;

    /** Plain free-text cell bound to one Row field. */
    function textCell(index: number, field: keyof Row, value: string, ok: boolean, errorMsg: string) {
        return (
            <td>
                <input
                    className={inputCls(ok)}
                    value={value}
                    aria-label={`${field}-${index + 1}`}
                    title={ok ? undefined : errorMsg}
                    onChange={(e) => updateRow(index, { [field]: e.target.value } as Partial<Row>)}
                />
            </td>
        );
    }

    /** Searchable dropdown cell constrained to pre-registered values. */
    function selectCell(
        options: { value: string; label: string }[],
        value: string,
        onChange: (v: string) => void,
        ok: boolean,
        ariaLabel: string,
        disabled?: boolean,
    ) {
        return (
            <td title={ok ? undefined : t('import.pickValid')}>
                <Select<{ value: string; label: string }, false>
                    options={options}
                    value={options.find((o) => o.value === value) ?? null}
                    onChange={(o) => onChange(o?.value ?? '')}
                    isDisabled={disabled}
                    isClearable
                    aria-label={ariaLabel}
                    classNamePrefix="dkn-rs"
                    className={`csv-grid-select${ok ? '' : ' is-invalid'}`}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 1060 }) }}
                    placeholder="—"
                    noOptionsMessage={() => t('common.noOptions')}
                />
            </td>
        );
    }

    async function submit() {
        if (!rows) return;
        setLoading(true);
        setServerError('');
        setSyncResults(null);
        // Split each row's E.164 phone back into the country code + national number
        // the backend expects.
        const dataToSend = rows
            .filter((_, i) => checked[i])
            .map((d) => ({ ...d, ...splitPhone(d.phone) }));
        try {
            const res = await post<SyncResult[]>('home/syncUsers', dataToSend);
            if (res.status === 200 && res.data && res.data.length) {
                setSyncResults(res.data);
            } else {
                setRows(null);
                setSuccess(t('import.synced'));
            }
        } catch {
            setServerError(t('import.serverError'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="pt-3">
            <p className="text-body-secondary">{t('import.description')}</p>

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}
            {serverError && (
                <Alert variant="danger" dismissible onClose={() => setServerError('')}>
                    {serverError}
                </Alert>
            )}

            <CSVReader onUploadAccepted={onUploadAccepted} config={{ header: true, skipEmptyLines: true }}>
                {({ getRootProps, acceptedFile, getRemoveFileProps }: any) => {
                    const removeProps = getRemoveFileProps();
                    return (
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <Button variant="outline-primary" type="button" {...getRootProps()}>
                                {acceptedFile ? acceptedFile.name : t('import.clickUpload')}
                            </Button>
                            {acceptedFile && (
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    {...removeProps}
                                    onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                        removeProps.onClick?.(e);
                                        setRows(null);
                                    }}
                                >
                                    {t('import.remove')}
                                </Button>
                            )}
                        </div>
                    );
                }}
            </CSVReader>

            {syncResults && (
                <>
                    <Alert variant="danger">{t('import.stopped')}</Alert>
                    <div className="table-responsive">
                        <Table bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>{t('import.colRow')}</th>
                                    <th>{t('import.colUser')}</th>
                                    <th>{t('import.colApp')}</th>
                                    <th>{t('import.colError')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {syncResults.map((d, i) => (
                                    <tr key={i}>
                                        <td>{d.rowNumber}</td>
                                        <td>{d.userName}</td>
                                        <td>{d.applicationName}</td>
                                        <td className="text-danger-emphasis">{d.error}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            )}

            {rows && rows.length > 0 && (
                <>
                    {invalidCount > 0 && (
                        <Alert variant="warning" className="py-2">
                            {t('import.fixErrors', { count: invalidCount })}
                        </Alert>
                    )}
                    <div className="table-responsive">
                        <Table striped bordered hover size="sm" className="csv-preview">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('register.firstName')}</th>
                                    <th>{t('register.lastName')}</th>
                                    <th>{t('register.email')}</th>
                                    <th>Customer id</th>
                                    <th>{t('register.phoneNumber')}</th>
                                    <th>ssApplicationServer</th>
                                    <th>ssCompany</th>
                                    <th>ssCompanyPwd</th>
                                    <th>ssPwd</th>
                                    <th>ssStoreId</th>
                                    <th>ssUser</th>
                                    <th>isJasper</th>
                                    <th>jasperApp</th>
                                    <th>{t('import.mapToSso')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((d, index) => (
                                    <tr key={d.key}>
                                        <td className="text-center">{index + 1}</td>
                                        {textCell(
                                            index,
                                            'firstName',
                                            d.firstName,
                                            d.firstName.trim() !== '',
                                            t('import.required'),
                                        )}
                                        {textCell(
                                            index,
                                            'lastName',
                                            d.lastName,
                                            d.lastName.trim() !== '',
                                            t('import.required'),
                                        )}
                                        <td>
                                            <input
                                                className={inputCls(isValidEmail(d.email))}
                                                value={d.email}
                                                aria-label={`email-${index + 1}`}
                                                title={isValidEmail(d.email) ? undefined : t('import.invalidEmail')}
                                                onChange={(e) =>
                                                    updateRow(index, {
                                                        email: e.target.value,
                                                        userName: e.target.value,
                                                    })
                                                }
                                            />
                                        </td>
                                        {selectCell(
                                            customers,
                                            d.office24SevenCustomerId,
                                            (v) => updateRow(index, { office24SevenCustomerId: v }),
                                            customerOk(d),
                                            `customer-${index + 1}`,
                                        )}
                                        <td title={isValidPhoneNumber(d.phone) ? undefined : t('import.invalidPhone')}>
                                            <PhoneInput
                                                defaultCountry="NO"
                                                value={d.phone || undefined}
                                                onChange={(v) => updateRow(index, { phone: v ?? '' })}
                                                countrySelectComponent={PhoneCountrySelect}
                                                className="dkn-phone dkn-phone--grid"
                                                numberInputProps={{
                                                    className: inputCls(isValidPhoneNumber(d.phone)),
                                                    'aria-label': `phone-${index + 1}`,
                                                }}
                                            />
                                        </td>
                                        {selectCell(
                                            ssOptions,
                                            d.applicationName,
                                            (v) => updateRow(index, { applicationName: v }),
                                            appOk(d),
                                            `ssApplicationServer-${index + 1}`,
                                        )}
                                        {textCell(
                                            index,
                                            'companyId',
                                            d.companyId,
                                            d.companyId !== '',
                                            t('import.required'),
                                        )}
                                        {textCell(index, 'companyPassword', d.companyPassword, true, '')}
                                        {textCell(
                                            index,
                                            'userPassword',
                                            d.userPassword,
                                            d.userPassword !== '',
                                            t('import.required'),
                                        )}
                                        {textCell(
                                            index,
                                            'storeId',
                                            d.storeId,
                                            d.storeId !== '',
                                            t('import.required'),
                                        )}
                                        {textCell(index, 'user', d.user, d.user !== '', t('import.required'))}
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={d.isJasper}
                                                aria-label={`isJasper-${index + 1}`}
                                                onChange={(e) =>
                                                    updateRow(index, {
                                                        isJasper: e.target.checked,
                                                        jasperName: e.target.checked ? d.jasperName : '',
                                                    })
                                                }
                                            />
                                        </td>
                                        {selectCell(
                                            jasperOptions,
                                            d.jasperName,
                                            (v) => updateRow(index, { jasperName: v }),
                                            jasperOk(d),
                                            `jasperApp-${index + 1}`,
                                            !d.isJasper,
                                        )}
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={checked[index] ?? false}
                                                onChange={() => toggle(index)}
                                                aria-label={`include-${index + 1}`}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            )}

            <SubmitButton type="button" onClick={submit} loading={loading} disabled={!rows || invalidCount > 0}>
                {t('import.submit')}
            </SubmitButton>
        </div>
    );
}
